import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import '../salaries.css';
import {
  PendingPaymentBranch,
  WorkingHours,
} from '../../../../api/types/accountants/salaries-pending-payments/salaries-pending-payments.type';
import globalSalaryStore from '../../../../stores/accountant/global-salary-store';
import { observer } from 'mobx-react-lite';
import dateUtils from '../../../../utils/date-format';

interface TravelExpensesTableProps {
  pendingPaymentByBranch: PendingPaymentBranch[];
}

interface TravelExpenseRow {
  id: number;
  date: Date;
  amount: number;
  workingHourId: number;
}

export const TravelExpensesTable: React.FC<TravelExpensesTableProps> = observer(
  ({ pendingPaymentByBranch }) => {
    // Find all selected working hours that have addTravel: true
    const travelExpenses = useMemo(() => {
      const selectedWorkingHourIds = new Set(
        globalSalaryStore.selectedWorkingHourToPay.map(
          (item) => item.workingHourId,
        ),
      );
      const excludedIds = new Set(
        globalSalaryStore.excludedTravelExpenseIdsArray,
      );

      // Group travel expenses by date (one per day, using the highest travel amount)
      const dailyTravelMap = new Map<
        string,
        {
          workingHourId: number;
          date: Date;
          amount: number;
        }
      >();

      // Iterate through all branches, courses, rates, and working hours
      pendingPaymentByBranch.forEach((branch) => {
        branch.courses.forEach((course) => {
          course.rates.forEach((rate) => {
            rate.workingHours.forEach((workingHour: WorkingHours) => {
              // Check if this working hour is selected, has addTravel: true, and is not excluded
              if (
                selectedWorkingHourIds.has(workingHour.id) &&
                workingHour.addTravel &&
                !excludedIds.has(workingHour.id)
              ) {
                const date = new Date(workingHour.startTime);
                const dateKey = `${date.getFullYear()}-${
                  date.getMonth() + 1
                }-${date.getDate()}`;
                const travelAmount = rate.travelRate ?? 0;
                const existing = dailyTravelMap.get(dateKey);

                if (!existing) {
                  dailyTravelMap.set(dateKey, {
                    workingHourId: workingHour.id,
                    date,
                    amount: travelAmount,
                  });
                } else {
                  let amount = existing.amount;
                  let workingHourId = existing.workingHourId;

                  if (travelAmount > existing.amount) {
                    amount = travelAmount;
                    workingHourId = workingHour.id;
                  }

                  dailyTravelMap.set(dateKey, {
                    workingHourId,
                    date: existing.date,
                    amount,
                  });
                }
              }
            });
          });
        });
      });

      const expenses: TravelExpenseRow[] = [];

      dailyTravelMap.forEach((value) => {
        if (value.amount > 0) {
          expenses.push({
            id: value.workingHourId,
            date: value.date,
            amount: value.amount,
            workingHourId: value.workingHourId,
          });
        }
      });

      return expenses;
    }, [
      pendingPaymentByBranch,
      globalSalaryStore.selectedWorkingHourToPay,
      globalSalaryStore.excludedTravelExpenseIdsArray,
    ]);

    const handleRemove = (rowData: TravelExpenseRow) => {
      // Exclude all travel expenses for this date (all working hours on that day),
      // but keep the working hours themselves selected
      const targetDate = rowData.date;
      const targetKey = `${targetDate.getFullYear()}-${
        targetDate.getMonth() + 1
      }-${targetDate.getDate()}`;

      const selectedWorkingHourIds = new Set(
        globalSalaryStore.selectedWorkingHourToPay.map(
          (item) => item.workingHourId,
        ),
      );

      pendingPaymentByBranch.forEach((branch) => {
        branch.courses.forEach((course) => {
          course.rates.forEach((rate) => {
            rate.workingHours.forEach((workingHour: WorkingHours) => {
              const date = new Date(workingHour.startTime);
              const dateKey = `${date.getFullYear()}-${
                date.getMonth() + 1
              }-${date.getDate()}`;

              if (
                dateKey === targetKey &&
                selectedWorkingHourIds.has(workingHour.id) &&
                workingHour.addTravel
              ) {
                globalSalaryStore.excludeTravelExpense(workingHour.id);
              }
            });
          });
        });
      });
    };

    const dateTemplate = (rowData: TravelExpenseRow): string => {
      return dateUtils.displayFormatDate(rowData.date);
    };

    const amountTemplate = (rowData: TravelExpenseRow): string => {
      return `₪${rowData.amount.toFixed(2)}`;
    };

    const removeButtonTemplate = (rowData: TravelExpenseRow) => {
      return (
        <Button
          icon="pi pi-times"
          severity="danger"
          rounded
          text
          onClick={() => handleRemove(rowData)}
          aria-label="הסר"
        />
      );
    };

    if (travelExpenses.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: '24px' }}>
        <h3
          style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}
        >
          נסיעות
        </h3>
        <div className="my-unique-datatable-container">
          <DataTable
            value={travelExpenses}
            tableStyle={{ minWidth: '30rem' }}
            stripedRows
            showGridlines
            dataKey="id"
          >
            <Column
              field="date"
              header="תאריך"
              sortable
              style={{ textAlign: 'right' }}
              body={dateTemplate}
            />
            <Column
              field="amount"
              header="סכום"
              sortable
              style={{ textAlign: 'right' }}
              body={amountTemplate}
            />
            <Column
              header="פעולות"
              style={{ textAlign: 'center', width: '100px' }}
              body={removeButtonTemplate}
            />
          </DataTable>
        </div>
      </div>
    );
  },
);
